import { useRef, useState } from 'react'
import { AvaAvatar } from './AvaAvatar'
import { auth } from '../../firebase'

/**
 * Floating "Chat with Ava" widget — bottom-right on every Control Center page.
 *
 * Mirrors the prototype's global `.ava-widget` (client-dashboard.html): a
 * gradient pill button with Ava's avatar + green online dot that expands into a
 * chat panel (header, message thread, quick-action suggestion pills, input box).
 *
 * Wired to the deployed `avaChat` Cloud Function via the same-origin `/api/ava-chat`
 * rewrite, with `context: 'control-center'` so Ava answers with the in-app system
 * prompt (buildControlCenterChatSystemParts) — the client's business, their hired
 * skills, and product knowledge. This is a CONVERSATIONAL channel: Ava explains,
 * guides, and answers here; actions the owner wants performed (pause a skill, send
 * a summary) are handled over the canonical SMS/voice channel, which is why the
 * quick-action pills are answerable questions, not action commands we can't execute
 * from this surface. On any transport error she falls back to pointing at SMS —
 * never a fabricated "done" (Operating Rule 5: no silent success).
 */

interface ChatMsg {
  id: number
  from: 'ava' | 'client'
  text: string
}

/**
 * Quick prompts Ava can genuinely answer from the control-center system prompt
 * (product knowledge + the client's hired skills). Deliberately NOT action verbs
 * like "Pause everything" — this endpoint is conversational and cannot execute
 * those, so offering them would overpromise (Rule 2).
 */
const SUGGESTIONS = ['What are my skills doing?', 'How do I add a skill?', 'How do I reach you by text?']

export interface AvaChatWidgetProps {
  /** Optional greeting shown as Ava's first message. */
  greeting?: string
  /** The owner's name — sent to Ava so she can address them and personalize. */
  clientName?: string
  /** The business name — grounds Ava's answers in the client's actual business. */
  businessName?: string
  /** Hired skill ids — so Ava knows what the client is actually paying for. */
  hiredAgents?: string[]
  /**
   * The owner's client id. Sent ONLY together with a Firebase ID token, and the
   * endpoint verifies the pair before trusting either — so this is an identity
   * claim, not a display field like the three above.
   *
   * Omitted (or sent without a signed-in user) means the turn is anonymous, which
   * is the correct and required behaviour for the unauthenticated ?demo= showcase.
   */
  clientId?: string
}

export function AvaChatWidget({
  greeting = "Hi! I'm Ava, your assistant. Ask me anything — or just text me at the number we set up.",
  clientName,
  businessName,
  hiredAgents,
  clientId,
}: AvaChatWidgetProps) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const nextId = useRef(1)
  const [messages, setMessages] = useState<ChatMsg[]>([
    { id: 0, from: 'ava', text: greeting },
  ])

  const push = (from: ChatMsg['from'], text: string) =>
    setMessages((prev) => [...prev, { id: nextId.current++, from, text }])

  const send = async (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || sending) return

    // History BEFORE this turn, in the shape avaChat expects ({role, content}).
    const history = messages.map((m) => ({
      role: m.from === 'client' ? ('user' as const) : ('assistant' as const),
      content: m.text,
    }))

    push('client', trimmed)
    setInput('')
    setSending(true)
    try {
      // Identity is sent as a PAIR — id + token — or not at all. Sending the id
      // without a token would just be rejected (the endpoint verifies the pair),
      // and sending a token without an id gives it nothing to authorize against.
      // No signed-in user is a legitimate state here: the ?demo= showcase renders
      // this widget with a fixture client and never authenticates.
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      let identity: { clientId?: string } = {}
      const user = auth.currentUser
      if (user && clientId) {
        try {
          headers.Authorization = `Bearer ${await user.getIdToken()}`
          identity = { clientId }
        } catch {
          // Token fetch failed — fall back to an anonymous turn rather than
          // failing the message. Ava still answers; only capture is lost.
          identity = {}
        }
      }

      const res = await fetch('/api/ava-chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: trimmed,
          history,
          context: 'control-center',
          clientName,
          businessName,
          hiredAgents,
          ...identity,
        }),
      })
      if (!res.ok) throw new Error(`ava-chat ${res.status}`)
      const data = (await res.json()) as { reply?: string }
      const reply = data.reply?.trim()
      push(
        'ava',
        reply ||
          "I didn't quite catch that — mind rephrasing? Or text me at the number we set up and I'll take care of it there."
      )
    } catch {
      push(
        'ava',
        "I'm having trouble connecting right now. Text me at the number we set up and I'll pick it right up — nothing gets lost."
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {open && (
        <div
          className="card rounded-xl shadow-xl mb-4 flex flex-col overflow-hidden w-[340px] max-w-[calc(100vw-3rem)]"
          style={{ height: '460px', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-blue-500 to-blue-600 text-white flex-shrink-0">
            <AvaAvatar size="sm" showOnlineDot={false} />
            <div className="min-w-0">
              <div className="font-semibold text-sm">Chat with Ava</div>
              <div className="text-xs text-blue-100">Your Assistant</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="ml-auto text-white/80 hover:text-white"
              aria-label="Close chat"
            >
              <i className="fas fa-xmark text-lg" />
            </button>
          </div>

          {/* Thread */}
          <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3" style={{ backgroundColor: 'var(--bg-body)' }}>
            {messages.map((msg) => (
              <div key={msg.id} className={msg.from === 'ava' ? 'flex gap-2' : 'flex gap-2 justify-end'}>
                {msg.from === 'ava' && <AvaAvatar size="sm" showOnlineDot={false} className="mt-0.5" />}
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                    msg.from === 'ava' ? 'theme-bg-card border theme-border theme-text-primary shadow-sm' : 'bg-blue-600 text-white'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex gap-2">
                <AvaAvatar size="sm" showOnlineDot={false} className="mt-0.5" />
                <div className="max-w-[80%] px-3 py-2 rounded-lg text-sm theme-bg-card border theme-border theme-text-secondary shadow-sm">
                  <span className="inline-flex gap-1" aria-label="Ava is typing">
                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Quick-action suggestion pills */}
          <div className="flex flex-wrap gap-1.5 px-3 py-2 border-t theme-border flex-shrink-0" style={{ backgroundColor: 'var(--bg-card)' }}>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                disabled={sending}
                className="text-xs px-2.5 py-1 rounded-full border theme-border theme-text-secondary hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &ldquo;{s}&rdquo;
              </button>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(input) }}
            className="flex items-center gap-2 px-3 py-2 border-t theme-border flex-shrink-0"
            style={{ backgroundColor: 'var(--bg-card)' }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="text"
              placeholder="Message Ava..."
              className="input flex-1"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Send"
            >
              <i className="fas fa-paper-plane text-sm" />
            </button>
          </form>
        </div>
      )}

      {/* Floating launcher button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform relative"
        style={{ padding: '3px' }}
        aria-label="Chat with Ava"
      >
        <img src={`${import.meta.env.BASE_URL}Ava2.png`} alt="Ava" className="w-full h-full rounded-full object-cover" />
        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
      </button>
    </div>
  )
}
